/**
 * Cmodal Component
 * Generic modal overlay system with blur backdrop, positioning options, and animations
 */
'use client';
import { useRef } from 'react';
import type { ReactElement } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Cblurbackdrop } from '../Cblurbackdrop';
import { ModalLocation } from '@/constants/componentPresets';
import { ColorType } from '@/theme/colors';
import { useViewport } from '@/hooks/dom';
import { Viewport } from '@/theme/breakpoints';
import { CmodalTile } from './CmodalTile';
import { getBackButtonStyle, getCmodalBackdropSettings, getModalContainerStyle, getModalContentWrapperStyle, getModalTileStyle, getModalTileWrapperStyle, } from './Cmodal.styles';
import type { CmodalProps } from './Cmodal.types';
import { dataEl } from '@/utils/dataEl';
import { useCmodalRegistration } from './useCmodalRegistration';
import { useCmodalTelemetry } from './useCmodalTelemetry';
export type { CmodalProps } from './Cmodal.types';
export function Cmodal({ dataElScope, visible, location = ModalLocation.Center, background = ColorType.Black, useContainerBackground = true, content, menuTitle, titleExtra, titleRightComponent, onClose, animationDuration = 250, padding = 16, backgroundOpacity = 0.05, }: CmodalProps): ReactElement {
    const { viewport } = useViewport();
    const isMobile = viewport === Viewport.Mobile;
    const containerRef = useRef<HTMLDivElement>(null);
    const contentWrapperRef = useRef<HTMLDivElement>(null);
    const tileWrapperRef = useRef<HTMLDivElement>(null);
    const tileRootRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const mainContentRef = useRef<HTMLDivElement>(null);
    const handleClose = useCmodalRegistration({ visible, onClose });
    const modalContainerStyle = getModalContainerStyle({ location });
    const modalContentWrapperStyle = getModalContentWrapperStyle({ location, isMobile });
    const modalTileWrapperStyle = getModalTileWrapperStyle({ location });
    const modalTileStyle = getModalTileStyle({ location });
    const backButtonStyle = getBackButtonStyle();
    const backdropSettings = getCmodalBackdropSettings({ location, animationDuration });
    useCmodalTelemetry({
        visible,
        location,
        menuTitle,
        titleExtra,
        isMobile,
        viewport,
        containerRef,
        contentWrapperRef,
        tileWrapperRef,
        tileRootRef,
        contentRef,
        mainContentRef,
    });
    return (
      <AnimatePresence>
        {visible && (<>
            <Cblurbackdrop visible={true} topOffset={backdropSettings.topOffset} opacity={backdropSettings.opacity} blur={backdropSettings.blur} useGradient={backdropSettings.useGradient} animationDuration={backdropSettings.animationDuration} onClick={handleClose}/>

            <div data-el={dataEl(dataElScope, 'Root')} ref={containerRef} style={modalContainerStyle}>
              <div data-el={dataEl(dataElScope, 'Content')} ref={contentWrapperRef} style={modalContentWrapperStyle}>
                <CmodalTile dataElScope={dataElScope} tileWrapperRef={tileWrapperRef} tileRootRef={tileRootRef} contentRef={contentRef} mainContentRef={mainContentRef} location={location} animationDuration={animationDuration} backButtonStyle={backButtonStyle} onClose={handleClose} modalTileWrapperStyle={modalTileWrapperStyle} modalTileStyle={modalTileStyle} padding={padding} useContainerBackground={useContainerBackground} background={background} backgroundOpacity={backgroundOpacity} content={content} menuTitle={menuTitle} titleExtra={titleExtra} titleRightComponent={titleRightComponent}/>
              </div>
            </div>
          </>)}
      </AnimatePresence>
    );
}
